import { AmaxPayShortLogo } from '@approvalmax/ui';
import { Flex, Popup, Spacing, Tabs } from '@approvalmax/ui/src/components';
import { domHelpers, hooks } from '@approvalmax/utils';
import { useAtom } from 'jotai';
import { memo, useCallback } from 'react';

import { fundWalletPopupState as fundWalletPopupStateAtom } from '../FundWalletPopup/FundWalletPopup.states';
import { FundWalletPopupContent } from '../FundWalletPopup/FundWalletPopupContent';
import { ConvertCurrencyPopup } from './components/ConvertCurrencyPopup/ConvertCurrencyPopup';
import { TransactionsTab } from './components/TransactionsTab/TransactionsTab';
import { WalletsTab } from './components/WalletsTab/WalletsTab';
import { WalletsTabActions } from './components/WalletsTabActions/WalletsTabActions';
import { messages } from './Wallets.messages';
import { TabKey } from './Wallets.types';

const qa = 'wallets';

export const Wallets = memo(() => {
    const [fundWalletPopupState, setFundWalletPopupState] = useAtom(fundWalletPopupStateAtom);

    const [activeTab, setActiveTab] = hooks.useQueryParam<TabKey>('activeTab', TabKey.Wallets, { required: true });

    const toggleFundWalletPopupOpen = useCallback(
        (open: boolean) => {
            if (!open) {
                setFundWalletPopupState({});
            }
        },
        [setFundWalletPopupState]
    );

    const handleTabChange = useCallback((tabKey: TabKey) => setActiveTab(tabKey), [setActiveTab]);

    return (
        <>
            <Tabs value={activeTab || TabKey.Wallets} onChange={handleTabChange}>
                <Flex justifyContent='space-between'>
                    <Flex alignItems='center' spacing='24'>
                        <AmaxPayShortLogo color='cobalt100' />

                        <Tabs.List bordered={false}>
                            <Tabs.Tab
                                variant='filled'
                                label={messages.wallets}
                                value={TabKey.Wallets}
                                key={TabKey.Wallets}
                                data-qa={domHelpers.generateDataQa(qa, 'wallets-tab')}
                            />

                            <Tabs.Tab
                                variant='filled'
                                label={messages.transactions}
                                value={TabKey.Transactions}
                                key={TabKey.Transactions}
                                data-qa={domHelpers.generateDataQa(qa, 'transactions-tab')}
                            />

                            <Tabs.Tab
                                variant='filled'
                                label={messages.members}
                                value={TabKey.Members}
                                key={TabKey.Members}
                                data-qa={domHelpers.generateDataQa(qa, 'members-tab')}
                            />
                        </Tabs.List>
                    </Flex>

                    {activeTab === TabKey.Wallets && <WalletsTabActions />}
                </Flex>

                <Spacing height={24} />

                <Tabs.Content value={TabKey.Wallets} key={TabKey.Wallets}>
                    <WalletsTab />
                </Tabs.Content>

                <Tabs.Content value={TabKey.Transactions} key={TabKey.Transactions}>
                    <TransactionsTab />
                </Tabs.Content>

                <Tabs.Content value={TabKey.Members} key={TabKey.Members} />
            </Tabs>

            <Popup open={!!fundWalletPopupState.walletId} onToggle={toggleFundWalletPopupOpen}>
                <FundWalletPopupContent />
            </Popup>

            <ConvertCurrencyPopup />
        </>
    );
});

Wallets.displayName = 'Wallets';
